<template>
  <div v-if="firmwareForm" class="py-8">
    <FirmwarePropertiesForm
        :form="firmwareForm"
        @on-save="confirmSave"
        @on-delete="confirmDelete"
        @on-download="downloadFirmware"
        @on-download-sig="downloadFirmwareSignature"
        @on-view-signature="viewSignature"
        :selected-firmware="$attrs.firmware"
    />

    <Dialog v-model:visible="signatureDialogVisible" modal :header="$t('forms.firmware.firmware_properties_form.signature')" :style="{ width: '50rem' }">
      <div v-if="signatureLoading" class="flex justify-center py-4">
        <ProgressSpinner style="width: 50px; height: 50px;" />
      </div>
      <div v-else-if="signatureError" class="text-red-500">
        {{ signatureError }}
      </div>
      <div v-else class="flex flex-col gap-4">
        <div class="font-mono text-xs bg-surface-100 dark:bg-surface-700 p-3 rounded-md break-all max-h-64 overflow-y-auto">
          {{ signatureContent }}
        </div>
        <div class="flex justify-between items-center">
          <span class="text-sm text-surface-600 dark:text-surface-400">
            {{ $t('forms.firmware.firmware_properties_form.signature_length', { length: signatureContent.length / 2 }) }}
          </span>
          <Button @click="copySignature" :label="$t('forms.firmware.firmware_properties_form.copy_signature')" severity="secondary" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import FirmwarePropertiesForm from "@/Forms/Firmware/FirmwarePropertiesForm.vue";
import {mapStores} from "pinia";
import {useFirmwaresStore} from "@/stores/firmwares.js";
import {useDownloadLink} from "@/Composables/DownloadLink.js";
import Dialog from "primevue/dialog";
import ProgressSpinner from "primevue/progressspinner";

export default {
  name: "FirmwarePropertiesView",
  components: {FirmwarePropertiesForm, Dialog, ProgressSpinner},
  data() {
    return {
      firmwareForm: undefined,
      signatureDialogVisible: false,
      signatureContent: '',
      signatureLoading: false,
      signatureError: null,
    }
  },
  emits: [
    'on-save',
    'on-delete',
  ],
  computed: {
    ...mapStores(useFirmwaresStore)
  },
  methods: {
    confirmSave() {
      if (this.firmwareForm?.regulation_ids.length > 0) {
        this.saveFirmware();
      } else {
        this.$confirm.require({
          group: 'save',
          message: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.are_you_sure_you_want_to_save_this_firmware_without_linking_regulations'),
          header: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.save_firmware'),
          accept: () => {
            this.saveFirmware();
          },
          reject: () => {
            this.$toast.add({
              severity: 'info',
              summary: this.$t('common.cancelled'),
              detail: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.firmware_was_not_saved'),
              life: 3000
            });
          }
        });
      }
    },
    async saveFirmware() {
      try {
        const response = await this.firmwareForm.submit();
        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.complete'),
          detail: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.firmware_saved'),
          life: 3000
        });
        this.$emit('on-save');
        this.$router.replace({params: {...this.$route.params, firmware: response.data.id}});
      } catch (error) {
        console.log('Error saving firmware', error);
      }
    },
    confirmDelete() {
      this.$confirm.require({
        group: 'delete',
        message: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.are_you_sure_you_want_to_delete_this_firmware'),
        header: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.delete_firmware'),
        accept: () => {
          this.deleteFirmware();
        },
        reject: () => {
          this.$toast.add({
            severity: 'info',
            summary: this.$t('common.cancelled'),
            detail: this.$t('pages.admin.firmware.firmware.firmware.properties.firmware_properties_view.firmware_was_not_deleted'),
            life: 3000
          });
        }
      });
    },
    async deleteFirmware() {
      try {
        if (this.$route.params.firmware !== 'NEW') {
          await this.firmwaresStore.deleteFirmware(this.$route.params.firmware);
          this.$emit('on-delete');
        }
        this.$router.push({name: 'admin.firmware', params: {...this.$route.params, firmware: undefined}});
      } catch (error) {
        console.log(error);
      }
    },
    async downloadFirmware(firmwareId) {
      try {
        const response = await this.firmwaresStore.showFirmware(firmwareId, {download: 1});
        useDownloadLink(response.url);
      } catch (error) {
        console.log('Error downloading firmware', error);
      }
    },
    async downloadFirmwareSignature(firmwareId) {
      try {
        const response = await this.firmwaresStore.showFirmware(firmwareId, {download: 1, signature_file: 1});
        useDownloadLink(response.url);
      } catch (error) {
        console.log('Error downloading firmware', error);
      }
    },
    async viewSignature(firmwareId) {
      this.signatureDialogVisible = true;
      this.signatureLoading = true;
      this.signatureError = null;
      this.signatureContent = '';
      try {
        const response = await this.firmwaresStore.showFirmware(firmwareId, {view_signature: 1});
        this.signatureContent = response.signature;
      } catch (error) {
        console.log('Error fetching signature', error);
        this.signatureError = this.$t('forms.firmware.firmware_properties_form.signature_error');
      } finally {
        this.signatureLoading = false;
      }
    },
    async copySignature() {
      if (this.signatureContent) {
        await navigator.clipboard.writeText(this.signatureContent);
        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.complete'),
          detail: this.$t('forms.firmware.firmware_properties_form.signature_copied'),
          life: 3000
        });
      }
    },
  },
  watch: {
    '$route.params.firmware': {
      async handler(newValue, oldValue) {
        if (newValue) {
          if (this.$route.params.firmware === 'NEW') {
            this.firmwareForm = await this.firmwaresStore.createFirmware();
          } else {
            this.firmwareForm = await this.firmwaresStore.editFirmware(this.$route.params.firmware);
            this.firmwareForm.id = this.$route.params.firmware;
          }
        } else {
          this.firmwareForm = undefined;
        }
      },
      immediate: true,
    }
  },
}
</script>
